//Imports
import React, { useEffect, useState } from "react";
import moment from 'moment';
import cx from 'classnames';

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
    Form,
    Input,
    Select,
    DatePicker,
    InputNumeric,
    TextArea
} from "@gpstrackit/gtc-react-controls";

//Components
import { NotificationManager } from "Components/Notifications";

//Slice
import {
    workOrdersSettingsSearchWorkOrderTypes,
    workOrdersSettingsCreateWorkOrder,
    workOrdersSettingsEditWorkOrder,
    workOrdersSettingsGetWorkOrder,
    resetWorkOrderSettings
} from "./redux/slice";
import {
    openModalWorkOrderCustomerSelector,
    openModalWorkOrderDriverSelector,
    resetWorkOrderCustomerSelector,
    resetWorkOrderDriverSelector
} from 'Redux/actions';

import {
    getFormattedDateByTimeZone,
    GetFormatForMoment
} from 'Core/data/Helpers';

//Views
import DriverSelector from '../../../driverSelector';
import CustomerSelector from '../../../customerSelector';

//Styles
import './index.scss';

const Settings = ({ refresh, createEditWorkOrderFormRef, close, changes, setChanges, customerId, preloadedData: propPreloadedData }) => {

    const { messages, locale } = useIntl();
    const dispatch = useDispatch();

    const minDate = new Date()
    const INIT_DATA = {
        scheduledAt: new Date(minDate.getTime() + 10 * 60000),
        customer: null,
        type: null,
        formName: null,
        estimatedDurationMins: null,
        severity: null,
        driver: null,
        dynamicFormId: null
    };

    //States
    const [formData, setFormData] = useState(INIT_DATA);
    const [errorCustomer, setErrorCustomer] = useState(false);
    const [errorDriver, setErrorDriver] = useState(false);
    const [isValidEstimatedTime, setIsValidEstimatedTime] = useState(true);
    const [closedDate, setClosedDate] = useState(null)
    const [updateScheduledDate, setUpdateScheduledDate] = useState(false)

    // Aplicar los datos preestablecidos al estado formData
    useEffect(() => {
        const dataToUse = propPreloadedData;
        if (dataToUse) {
            // Crear una copia de preloadedData para evitar modificar el objeto original
            const preloadedDataCopy = { ...dataToUse };
            
            // Asegurarse de que estimatedDurationMins es un número
            if (preloadedDataCopy.estimatedDurationMins) {
                preloadedDataCopy.estimatedDurationMins = Number(preloadedDataCopy.estimatedDurationMins);
            }
            
            // Actualizar el estado local
            setFormData(prevData => ({
                ...prevData,
                ...preloadedDataCopy
            }));
            
            // Asegurarse de que isValidEstimatedTime es true para que se muestre el input
            setIsValidEstimatedTime(true);
            
            // Actualizar explícitamente el formulario con los valores preestablecidos
            // Esto es crucial para que los campos controlados como InputNumeric se actualicen
            setTimeout(() => {
                createEditWorkOrderFormRef?.setValues(preloadedDataCopy);
            }, 100);
        }
    }, [propPreloadedData]);

    let severityList = [
        { id: "Low", name: messages['workOrder_createEdit_low'] },
        { id: "Medium", name: messages['workOrder_createEdit_medium'] },
        { id: "High", name: messages['workOrder_createEdit_high'] }
    ]

    //Redux
    const selectedDriver = useSelector((state) => state.workOrdersDriverSelectorRedux.selectedDriver);
    const openModalDriverSelector = useSelector((state) => state.workOrdersDriverSelectorRedux.open);
    const openModalCustomerSelector = useSelector((state) => state.workOrdersCustomerSelectorRedux.open);
    const selectedCustomer = useSelector((state) => state.workOrdersCustomerSelectorRedux.selectedCustomer);
    const { open, workOrderId } = useSelector((state) => state.workOrdersCreateEditWorkOrderRedux);
    const user = useSelector((state) => state.securityRedux.user);

    const {
        loadingWorkOrderTypes,
        workOrderTypes,
        workOrder,
        loading,
        create,
        update,
        error
    } = useSelector((state) => state.workOrdersSettingsWorkOrderRedux);

    //Handlers and utils
    const handleClose = () => {
        setUpdateScheduledDate(false)
        setFormData(INIT_DATA);
        close()
    };

    const handleSuccess = () => {
        NotificationManager.success(
            messages["workOrder_createEdit_success"],
            messages["success"],
            3000,
            null,
            null,
            "success"
        );
        if (typeof refresh === 'function') {
            refresh();
        }
        handleClose();
    };

    const handleCreateEditWorkOrder = () => {
        const currentDate = moment().utc();
        const startMoment = moment(formData?.scheduledAt).utc();

        if (updateScheduledDate && startMoment.isBefore(currentDate)) {
            NotificationManager.error(
                messages['workOrder_createEdit_scheduledDateError'],
                messages["error"],
                3000,
            );
            return
        }

        const isEditing = !!workOrderId;
        let payload = {};

        if (isEditing) {
            payload = {
                id: workOrderId,
                ...(changes.scheduledAt && { scheduledDate: startMoment.toISOString() }),
                ...(changes.driver && { driverId: formData?.driver?.id, scheduledDate: startMoment.toISOString(), estimatedDurationMins: formData?.estimatedDurationMins }),
                ...(changes.customer && {
                    customerId: formData?.customer?.id,
                    customerLocationId: formData?.customer?.customerLocation?.id,
                    customerContactId: formData?.customer?.customerContact?.id,
                }),
                ...(changes.type && {
                    workOrderTypeId: formData?.type?.id,
                    dynamicFormId: formData?.dynamicFormId,
                }),
                ...(changes.severity && {
                    severity: formData?.severity?.id,
                }),
                ...(changes.estimatedDurationMins && {
                    estimatedDurationMins: formData?.estimatedDurationMins,
                }),
                ...(changes.description && {
                    description: formData?.description,
                })
            }
        } else {
            payload = {
                customerId: formData?.customer?.id,
                scheduledDate: startMoment.toISOString(),
                driverId: formData?.driver?.id,
                customerLocationId: formData?.customer?.customerLocation?.id,
                customerContactId: formData?.customer?.customerContact?.id,
                workOrderTypeId: formData?.type?.id,
                severity: formData?.severity?.id,
                description: formData?.description,
                estimatedDurationMins: formData?.estimatedDurationMins,
                dynamicFormId: formData?.dynamicFormId
            }
        }

        if (!isEditing) {
            dispatch(workOrdersSettingsCreateWorkOrder(payload))
        } else {
            dispatch(workOrdersSettingsEditWorkOrder(payload))
        }
    }

    useEffect(() => {
        if (!openModalDriverSelector && selectedDriver) {
            setTimeout(() => {
                createEditWorkOrderFormRef?.setValues({
                    scheduledAt: selectedDriver?.scheduledAt,
                    driver: selectedDriver
                });
            }, 500)
            setChanges({ ...changes, driver: true })
            setFormData({ ...formData, driver: selectedDriver, scheduledAt: selectedDriver?.scheduledAt })
            dispatch(resetWorkOrderDriverSelector({ selectedDriver: null }))
        }
    }, [openModalDriverSelector, selectedDriver])

    useEffect(() => {
        if (open) {
            dispatch(workOrdersSettingsSearchWorkOrderTypes({}))
        }
    }, [open])

    useEffect(() => {
        if (!openModalCustomerSelector && selectedCustomer) {
            let customer = {
                ...selectedCustomer.customer,
                customerLocation: selectedCustomer.customerLocation,
                customerContact: selectedCustomer.customerContact,
            }
            setFormData({
                ...formData,
                customer: customer
            })
            setChanges({ ...changes, customer: true })
            setTimeout(() => {
                createEditWorkOrderFormRef?.setValues({ customer: customer });
            }, 500)
            dispatch(resetWorkOrderCustomerSelector({ selectedCustomer: null }))
        }
    }, [selectedCustomer, openModalCustomerSelector])

    useEffect(() => {
        if (workOrderId) {
            dispatch(workOrdersSettingsGetWorkOrder({ id: workOrderId }))
        }
    }, [workOrderId])

    useEffect(() => {
        if (workOrder) {
            let severity = severityList.find((item) => item?.id === workOrder?.severity)
            let payload = {
                scheduledAt: workOrder?.scheduledDate,
                customer: {
                    ...workOrder?.customer,
                    customerLocation: workOrder?.customerLocation,
                    customerContact: workOrder?.customerContact
                },
                type: {
                    id: workOrder?.workOrderType?.id,
                    name: workOrder?.workOrderType?.name
                },
                driver: {
                    id: workOrder?.driver?.id,
                    name: workOrder?.driver?.title,
                    unitId: workOrder?.driver?.unit?.id,
                    unitName: workOrder?.driver?.unit?.label,
                    avatarFileId: workOrder?.driver?.avatarFile?.id,
                    repositoryPath: workOrder?.driver?.avatarFile?.repositoryPath,
                    fileKey: workOrder?.driver?.avatarFile?.fileKey
                },
                severity: severity,
                formName: workOrder?.workOrderType?.dynamicForm?.name,
                estimatedDurationMins: workOrder?.estimatedDurationMins,
                description: workOrder?.description
            }
            createEditWorkOrderFormRef?.setValues(payload);
            setFormData({ ...formData, ...payload })
        }
    }, [workOrder])

    useEffect(() => {
        if (!isValidEstimatedTime) {
            setIsValidEstimatedTime(true)
        }
    }, [isValidEstimatedTime])

    useEffect(() => {
        if (create || update) {
            handleSuccess();
        }
        if (formData?.scheduledAt && formData?.estimatedDurationMins) {
            const closedDate = moment(formData?.scheduledAt).add(formData?.estimatedDurationMins, 'minutes').utc();
            const momentFormat = user?.dateformat ? `${GetFormatForMoment(user.dateformat)} h:mm a z` : null;
            let date = getFormattedDateByTimeZone(closedDate, user?.timeZone, messages, momentFormat, false, locale);
            setClosedDate(date)
        }
    }, [create, update, formData?.scheduledAt, formData?.estimatedDurationMins])

    useEffect(() => {
        if (error) {
            let errorMessage = error?.error || error?.message || messages['errorMessage']

            if (errorMessage.includes("no dynamic form")) {
                errorMessage = messages['workOrder_createEdit_formError']
            } else if (errorMessage.includes("scheduledDate must be greater than current date")) {
                errorMessage = messages['workOrder_createEdit_dateError']
            } else if (errorMessage.includes("driver not available")) {
                errorMessage = messages['workOrder_createEdit_driverError']
            }
            NotificationManager.error(
                errorMessage,
                messages['error'],
                3000
            );
            dispatch(resetWorkOrderSettings({ error: null }))
        }
    }, [error])

    return (
        <div style={{ padding: !workOrderId && 24 }}>
            <Form
                ref={createEditWorkOrderFormRef.ref}
                loading={loading || loadingWorkOrderTypes}
                onValidSubmit={() => {
                    if (formData?.customer && formData?.driver) {
                        handleCreateEditWorkOrder();
                    }
                }}
                onChangeValues={() => { }}
                onSubmit={() => {
                    if (!formData?.customer) {
                        setErrorCustomer(true)
                    }
                    if (!formData?.driver) {
                        setErrorDriver(true)
                    }
                }}
            >
                <div style={{ marginBottom: 15 }}>
                    <div style={{ marginBottom: 5 }}><span className="required-gtc-rc">*</span><label className="label-gtc-rc">{messages['workOrder_createEdit_scheduledAt']}</label></div>
                    <DatePicker
                        id="scheduledAt"
                        minDate={new Date() || null}
                        placeholder={messages['workOrder_createEdit_scheduledAt']}
                        value={formData?.scheduledAt}
                        showTimePicker
                        onChange={(value) => {
                            setUpdateScheduledDate(true)
                            setFormData({ ...formData, scheduledAt: value })
                            setChanges({ ...changes, scheduledAt: true, driver: true })
                        }}
                    />
                </div>

                <div style={{ marginBottom: 15 }}>
                    <div style={{ marginBottom: 5 }}><span className="required-gtc-rc">*</span><label className="label-gtc-rc">{messages['workOrder_createEdit_customerLocation']}</label></div>
                    <div
                        className={cx("work-order-settings-customer", errorCustomer && "work-order-settings-customer-error")}
                        onClick={() => {
                            dispatch(openModalWorkOrderCustomerSelector({ selectedCustomer: formData.customer }));
                            setErrorCustomer(false);
                        }}
                    >
                        {
                            formData.customer?.name && (
                                <>
                                    <div className="work-order-settings-customer-name">{formData.customer?.name}</div>
                                    <div> {`${formData?.customer?.customerContact?.email}${formData?.customer?.customerContact?.email && formData?.customer?.customerContact?.phone ? ' - ' : ''}${formData?.customer?.customerContact?.phone}`}</div>
                                    <div> {formData.customer?.customerLocation?.name}</div>
                                </>
                            )
                        }
                    </div>
                    {
                        errorCustomer ? (
                            <label className="assistive-gtc-rc-text assistive-gtc-rc-text-error" style={{ marginTop: 5 }}>{messages['required_field']}</label>
                        ) : <></>
                    }
                </div>
                <Form.Item
                    name="type"
                    label={messages['workOrder_createEdit_type']}
                    rules={[{ required: true, message: 'required_field' }]}
                >
                    <Select
                        id="type"
                        options={workOrderTypes || []}
                        placeholder={messages['workOrder_createEdit_type']}
                        valueField="id"
                        textField="name"
                        style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                        onChange={(value) => {
                            if (!propPreloadedData?.estimatedDurationMins && !propPreloadedData?.estimatedDurationMins > 0) {
                                setTimeout(() => {
                                    createEditWorkOrderFormRef?.setValues({
                                        formName: value?.dynamicForm?.name,
                                        estimatedDurationMins: value?.estimatedDurationMins,
                                        driver: null,
                                        dynamicFormId: value?.dynamicFormId
                                    });
                                }, 500)
                                setFormData({
                                    ...formData,
                                    type: value,
                                    estimatedDurationMins: value?.estimatedDurationMins,
                                    driver: null,
                                    dynamicFormId: value?.dynamicFormId
                                })
                            } else {
                                setFormData({
                                    ...formData,
                                    type: value
                                })
                            }
                            setChanges({ ...changes, type: true })
                            setIsValidEstimatedTime(false)
                        }}
                        isSearchable
                        isClearable
                        isDisabled={workOrder?.dynamicFormAnswerId}
                    />
                </Form.Item>
                <Form.Item
                    name="formName"
                    label={messages['workOrder_createEdit_formName']}
                >
                    <Input
                        id="formName"
                        type="text"
                        placeholder={messages['workOrder_createEdit_formName']}
                        onChange={(value) => { }}
                        maxLength={20}
                        isDisabled
                    />
                </Form.Item>
                {
                    isValidEstimatedTime ? (
                        <Form.Item
                            name="estimatedDurationMins"
                            label={messages['workOrder_createEdit_estimatedDuration']}
                            rules={[
                                {
                                    required: true,
                                    message: messages['required_field']
                                },
                                {
                                    min: 1,
                                    message: `${messages['min']}: 1`
                                },
                                {
                                    max: 43800,
                                    message: `${messages['max']}: 43800`
                                }
                            ]}
                            initialValue={formData?.estimatedDurationMins}
                        >
                            <InputNumeric
                                id="estimatedDurationMins"
                                type="number"
                                isInteger={true}
                                placeholder={messages['workOrder_createEdit_estimatedDuration']}
                                onChange={(value) => {
                                    setFormData({ ...formData, estimatedDurationMins: value, driver: null })
                                    setTimeout(() => createEditWorkOrderFormRef?.setValues({ driver: null }), 500)
                                    setChanges({ ...changes, estimatedDurationMins: true })
                                }}
                                maxLength={5}
                                min={1}
                                max={43800}
                                onlyPositive
                                assistiveText={formData?.estimatedDurationMins && closedDate ? `${messages['workOrder_createEdit_closeDate']}: ${closedDate}` : ''}
                            />
                        </Form.Item>
                    ) : <></>
                }
                <div style={{ marginBottom: 15 }}>
                    <div style={{ marginBottom: 5 }}><span className="required-gtc-rc">*</span><label className="label-gtc-rc">{messages['workOrder_createEdit_driver']}</label></div>
                    <div
                        className={cx("work-order-settings-driver", errorDriver && "work-order-settings-driver-error")}
                        style={{
                            cursor: !(formData.scheduledAt && formData.estimatedDurationMins && formData?.customer) ? 'not-allowed' : 'context-menu',
                            backgroundColor: !(formData.scheduledAt && formData.estimatedDurationMins && formData?.customer) && '#F6F6F6'
                        }}
                        onClick={() => {
                            if (formData.scheduledAt && formData.estimatedDurationMins && formData?.customer) {
                                dispatch(openModalWorkOrderDriverSelector({
                                    scheduledAt: formData?.scheduledAt,
                                    estimatedDurationMins: formData?.estimatedDurationMins,
                                    driverId: formData?.driver?.id,
                                    driverName: formData?.driver?.name,
                                    driver: formData?.driver,
                                    tagIds: formData?.customer?.tagIds || []
                                }))
                                setErrorDriver(false)
                            }
                        }}
                    >
                        {
                            formData.driver?.name && (
                                <>
                                    <div className="work-order-settings-driver-name">{formData.driver?.name}</div>
                                    <div ><strong>{messages['workOrder_createEdit_unit']}</strong> {formData.driver?.unitName || messages['workOrder_createEdit_unasigned']}</div>
                                </>
                            )
                        }
                    </div>
                    {
                        errorDriver ? (
                            <label className="assistive-gtc-rc-text assistive-gtc-rc-text-error" style={{ marginTop: 5 }}>{messages['required_field']}</label>
                        ) : <></>
                    }
                </div>
                <Form.Item
                    name="severity"
                    label={messages['workOrder_createEdit_severity']}
                    rules={[{ required: true, message: 'required_field' }]}
                >
                    <Select
                        id="severity"
                        options={severityList || []}
                        placeholder={messages['workOrder_createEdit_severity']}
                        valueField="id"
                        textField="name"
                        style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                        isSearchable
                        onChange={(value) => {
                            setFormData({ ...formData, severity: value })
                            setChanges({ ...changes, severity: true })
                        }}
                    />
                </Form.Item>
                <Form.Item
                    name="description"
                    label={messages['workOrder_createEdit_description']}
                >
                    <TextArea
                        id="description"
                        maxLength={500}
                        onChange={(value) => {
                            setChanges({ ...changes, description: true })
                            setFormData({ ...formData, description: value })
                        }}
                    />
                </Form.Item>
            </Form>
            {openModalDriverSelector && <DriverSelector />}
            {openModalCustomerSelector && <CustomerSelector currentId={customerId} />}
        </div>
    );
};

export default Settings;